import { useDispatch, useSelector } from "react-redux";
import { setAnalyticsOverview, setAnalyticsTimeframe } from "../context";
import { useEffect, useMemo, useState } from "react";
import { GroupAnalyticsService } from "../services/group-analytics.service";
import { axiosInstance } from "lib/axios";
import { useUserData } from "features/User";
import { ALL_PARENT_CHILD_GROUPS } from "./parent-child-groups.hook";

const GROUP_TYPES = Object.freeze({
  PARENT: 0,
});

export function useGroupAnalytics() {
  const analyticsService = useMemo(
    () => new GroupAnalyticsService(axiosInstance),
    []
  );
  const dispatch = useDispatch();
  const { groupId, groupType } = useUserData();
  const {
    analyticsTimeframe,
    analyticsOverview,
    parentChildGroups,
    selectedParentChildGroupId,
  } = useSelector((state) => state.group);
  const [overviewDataError, setOverviewDataError] = useState(null);
  const [loading, setLoading] = useState(false);
  const isParentGroup = groupType === GROUP_TYPES.PARENT;
  const isAllGroupsSelected =
    isParentGroup && selectedParentChildGroupId === ALL_PARENT_CHILD_GROUPS;
  const selectedOrganizationIds = useMemo(() => {
    if (isAllGroupsSelected) {
      return parentChildGroups
        .map((group) => Number(group.organizationId))
        .filter((organizationId) => !Number.isNaN(organizationId));
    }

    if (isParentGroup && selectedParentChildGroupId != null) {
      return [Number(selectedParentChildGroupId)];
    }

    return groupId != null ? [Number(groupId)] : [];
  }, [
    groupId,
    isAllGroupsSelected,
    isParentGroup,
    parentChildGroups,
    selectedParentChildGroupId,
  ]);
  const isAnalyticsRequestReady =
    selectedOrganizationIds.length > 0 &&
    (!isParentGroup || selectedParentChildGroupId != null);

  useEffect(() => {
    async function fetchData() {
      if (!isAnalyticsRequestReady) {
        return;
      }

      setOverviewDataError(null);
      setLoading(true);
      try {
        const data = await analyticsService.getAnalyticsOverviewData({
          dataFrequency: analyticsTimeframe,
          selectedOrganizationIds,
        });
        dispatch(setAnalyticsOverview(data));
      } catch (error) {
        setOverviewDataError(error);
        dispatch(setAnalyticsOverview([]));
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [
    analyticsService,
    analyticsTimeframe,
    dispatch,
    isAllGroupsSelected,
    isAnalyticsRequestReady,
    selectedOrganizationIds,
  ]);

  return {
    analyticsOverview,
    loading,
    overviewDataError,
    timeframe: analyticsTimeframe,
    setTimeframe: (timeframe) => {
      dispatch(setAnalyticsTimeframe(timeframe));
    },
  };
}
